import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getSeasonRatesAsync,
  selectSeasonRateItems,
  selectSeasonRateItemsLoadingStatus,
} from './seasonRatesSlice';
import { Loader } from '../../common/components';
import { LOADING_STATUS } from '../../common/constants';
import SeasonRatePickerList from './Components/SeasonRatePickerList';
import { formatDateToYYYYMMDDWithoutTime } from '../../common/utils/dateUtils';

interface SeasonRatePickerContainerProps {
  selectedAccommodationId: string;
  onSelectedIdsChange: (selectedIds: string[]) => void;
}

const SeasonRatePickerContainer: React.FC<SeasonRatePickerContainerProps> = ({
  selectedAccommodationId,
  onSelectedIdsChange,
}) => {
  const dispatch = useAppDispatch();
  const seasonRateItems = useAppSelector(selectSeasonRateItems);
  const seasonRateItemsLoadingStatus = useAppSelector(
    selectSeasonRateItemsLoadingStatus,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (seasonRateItems.length === 0) {
      setSelectedIds([]);
      return;
    }

    const availableIds = new Set(seasonRateItems.map(item => item.id));
    setSelectedIds(prevSelectedIds =>
      prevSelectedIds.filter(id => availableIds.has(id)),
    );
  }, [seasonRateItems]);

  useEffect(() => {
    if (selectedAccommodationId) {
      const currentDate = formatDateToYYYYMMDDWithoutTime(new Date());
      dispatch(
        getSeasonRatesAsync({
          accommodationId: selectedAccommodationId,
          startFetchingRatesFrom: currentDate,
        }),
      );
    }
  }, [dispatch, selectedAccommodationId]);

  useEffect(() => {
    onSelectedIdsChange(selectedIds);
  }, [selectedIds, onSelectedIdsChange]);

  const handleCheckboxChange = (id: string, isChecked: boolean) => {
    setSelectedIds(prevSelectedIds =>
      isChecked
        ? [...prevSelectedIds, id]
        : prevSelectedIds.filter(selectedId => selectedId !== id),
    );
  };

  const handleToggleAll = (isChecked: boolean) => {
    setSelectedIds(isChecked ? seasonRateItems.map(item => item.id) : []);
  };

  const isAllSelected =
    seasonRateItems.length > 0 &&
    seasonRateItems.every(item => selectedIds.includes(item.id));

  return (
    <>
      <Loader
        IsLoading={seasonRateItemsLoadingStatus === LOADING_STATUS.LOADING}
      />
      {seasonRateItemsLoadingStatus === LOADING_STATUS.IDLE && (
        <SeasonRatePickerList
          seasonRateItems={seasonRateItems}
          onCheckboxChange={handleCheckboxChange}
          onToggleAll={handleToggleAll}
          isAllSelected={isAllSelected}
          selectedIds={selectedIds}
        />
      )}
    </>
  );
};

export default SeasonRatePickerContainer;
